import { useEffect } from "react";
import { useAppContext } from "../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../classes/contexts/PatientContext";
import { usePlanContext } from "../../../classes/contexts/PlanContext";
import { ButtonSize } from "../../../classes/enums/ButtonEnum";
import { DataPermissions } from "../../../classes/enums/PermissionsEnum";
import IconsEnum from "../../../classes/enums/IconsEnum";
import IconButton from "../../../components/iconButton/IconButton";
import { iconMapping } from "../../../config/iconConfig";
import { useAuth } from "../../../hooks/useAuth";
import { usePlans } from "../../../hooks/queries/usePlans";
import { useDataBundle } from "../../../hooks/useDigitalTwinDataBundle";
import { useRouteState } from "../../../hooks/useRouteState";
import DeletePlanModal from "./DeletePlanModal";
import "./PlanTab.css";

const PlanTab = ({ persistedSelectedPlanId: persistedSelectedPlanIdProp }) => {
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const canDelete = checkPermissions(DataPermissions.DELETE_PLAN);
  const { refreshPatientContext } = usePatientContext();
  const { selectedTwin } = useDataBundle();
  const { params, navigateToAlign, navigateToAlignComparison } =
    useRouteState();
  const {
    currentPlanId,
    updateCurrentPlanId,
    comparedPlanId,
    updateComparedPlanId,
    resetComparedPlanId,
  } = usePlanContext();
  const selectedDigitalTwin = selectedTwin?.[0] || null;
  const plansIds = selectedDigitalTwin?.plans || [];
  const persistedSelectedPlanId =
    persistedSelectedPlanIdProp ?? selectedDigitalTwin?.selected_plan ?? null;
  const VerifiedIcon = iconMapping[IconsEnum.CONFIRM];
  const { plans, createPlan, duplicatePlan, refetchPlans } = usePlans(
    plansIds,
    selectedDigitalTwin?._id
  );
  const isPlanLimitReached = plans.length >= 3;
  const isComparisonMode = params.compareId != null;
  const canCompare = plans.length >= 2;

  useEffect(() => {
    if (params.planId && params.planId !== currentPlanId) {
      updateCurrentPlanId(params.planId);
    } else if (!params.planId && plans.length > 0 && !currentPlanId) {
      updateCurrentPlanId(plans[0]._id);
      navigateToAlign(params.patientId, params.twinId, plans[0]._id);
    }
  }, [
    params.planId,
    currentPlanId,
    updateCurrentPlanId,
    plans,
    params.patientId,
    params.twinId,
    navigateToAlign,
  ]);

  useEffect(() => {
    if (params.compareId && params.compareId !== comparedPlanId) {
      updateComparedPlanId(params.compareId);
    } else if (!params.compareId && comparedPlanId) {
      resetComparedPlanId();
    }
  }, [
    params.compareId,
    comparedPlanId,
    updateComparedPlanId,
    resetComparedPlanId,
  ]);

  const handlePlanClick = planId => {
    const plan = plans.find(p => p._id === planId);
    if (!plan) {
      return;
    }

    if (isComparisonMode && params.patientId && params.twinId) {
      const currentPlanFromParams = params.planId;
      const comparedPlanFromParams = params.compareId;

      if (planId === currentPlanFromParams) {
        updateCurrentPlanId(plan._id);
        return;
      }

      if (planId === comparedPlanFromParams) {
        updateCurrentPlanId(plan._id);
        if (currentPlanFromParams) {
          updateComparedPlanId(currentPlanFromParams);
          navigateToAlignComparison(
            params.patientId,
            params.twinId,
            plan._id,
            currentPlanFromParams
          );
        }
        return;
      }

      if (currentPlanFromParams) {
        updateComparedPlanId(plan._id);
        navigateToAlignComparison(
          params.patientId,
          params.twinId,
          currentPlanFromParams,
          plan._id
        );
      }
      return;
    }

    updateCurrentPlanId(plan._id);
    navigateToAlign(params.patientId, params.twinId, plan._id);
  };

  const handleAddPlan = async () => {
    if (isPlanLimitReached) {
      return;
    }
    const newPlanId = await createPlan();
    if (newPlanId) {
      updateCurrentPlanId(newPlanId);
      navigateToAlign(params.patientId, params.twinId, newPlanId);
    }
  };

  const handleDuplicatePlan = async planId => {
    if (isPlanLimitReached) {
      return;
    }
    const planToDuplicate = plans.find(plan => planId === plan._id);
    if (!planToDuplicate) {
      return;
    }

    const newPlanId = await duplicatePlan(planToDuplicate);
    if (newPlanId) {
      await refreshPatientContext();
      updateCurrentPlanId(newPlanId);
      navigateToAlign(params.patientId, params.twinId, newPlanId);
    }
  };

  const handleComparePlans = () => {
    if (!canCompare) {
      return;
    }

    if (isComparisonMode) {
      navigateToAlign(params.patientId, params.twinId, params.planId);
      return;
    }

    const defaultComparePlanId = plans.find(
      plan => plan._id !== params.planId
    )?._id;

    if (defaultComparePlanId) {
      navigateToAlignComparison(
        params.patientId,
        params.twinId,
        params.planId,
        defaultComparePlanId
      );
    }
  };

  const handlePlanDeleted = async deletedPlanId => {
    const remainingPlans = plans.filter(plan => plan._id !== deletedPlanId);
    await refreshPatientContext();
    await refetchPlans();

    if (remainingPlans.length === 0) {
      resetComparedPlanId();
      updateCurrentPlanId(null);
      return;
    }

    const firstPlanId = remainingPlans[0]._id;
    resetComparedPlanId();
    updateCurrentPlanId(firstPlanId);
    navigateToAlign(params.patientId, params.twinId, firstPlanId);
  };

  return (
    <div className="plan-tab">
      <div className="plan-tab-plans-wrapper">
        {plans.map((plan, index) => {
          const isCurrent = currentPlanId === plan._id;
          const isPersistedSelected = plan._id === persistedSelectedPlanId;
          const isCompared = comparedPlanId === plan._id;

          return (
            <div
              key={plan._id}
              className={`plan-button-container ${
                isCompared ? "compared" : ""
              }`}
            >
              <button
                className={`plan-button ${isCurrent ? "active" : "inactive"} ${
                  isPersistedSelected ? "persisted-selected" : ""
                }`}
                style={{ "--plan-index": index }}
                onClick={() => handlePlanClick(plan._id)}
              >
                {isPersistedSelected && VerifiedIcon && (
                  <span className="plan-verified-icon">
                    <VerifiedIcon />
                  </span>
                )}
                {plan.name || `Plan ${index + 1}`}
              </button>
              {canDelete && plans.length > 1 && !isComparisonMode && (
                <div className={`plan-button-delete ${isCurrent ? "active" : ""}`}>
                  <IconButton
                    icon={IconsEnum.DELETE}
                    size={ButtonSize.SMALL}
                    noBgColor={true}
                    className={`plan-delete-icon ${isCurrent ? "active" : ""}`}
                    title="Delete Plan"
                    onClick={e => {
                      e.stopPropagation();
                      showPopup(
                        <DeletePlanModal
                          plan={plan}
                          onPlanDeleted={handlePlanDeleted}
                        />
                      );
                    }}
                  />
                </div>
              )}
              <div
                className={`plan-button-duplicate ${isCurrent ? "active" : ""}`}
              >
                <IconButton
                  icon={IconsEnum.DUPLICATE}
                  size={ButtonSize.SMALL}
                  onClick={() => handleDuplicatePlan(plan._id)}
                  title={`Duplicate ${plan.name || `Plan ${index + 1}`}`}
                  noBgColor={true}
                  className={`plan-duplicate-icon ${isCurrent ? "active" : ""}`}
                  disabled={isPlanLimitReached || isComparisonMode}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="plan-tab-actions">
        <div className="plan-tab-actions-add">
          <IconButton
            icon={IconsEnum.ADD}
            size={ButtonSize.SMALL}
            onClick={handleAddPlan}
            title="Add New Plan"
            noBgColor={true}
            disabled={isPlanLimitReached || isComparisonMode}
          />
        </div>
        <div
          className={`plan-tab-actions-compare ${isComparisonMode ? "active" : ""}`}
        >
          <IconButton
            icon={IconsEnum.COMPARE}
            size={ButtonSize.SMALL}
            onClick={handleComparePlans}
            title={isComparisonMode ? "Exit Comparison" : "Compare Plans"}
            noBgColor={true}
            isTogglable={true}
            isToggled={isComparisonMode}
            disabled={!canCompare}
          />
        </div>
      </div>
    </div>
  );
};

export default PlanTab;
